/**
 * DepartementHeatmap — V14.68.9 (Feb 2026)
 *
 * Displays a choropleth of French metropolitan départements colored by the
 * count of user's HUB addresses in each. Fetches the GeoJSON boundaries lazily
 * from the public france-geojson CDN (Etalab open data, ~150 KB).
 *
 * Matching strategy: the Nominatim `departement` field returns the human name
 * ("Loire-Atlantique", "Finistère"…). The GeoJSON `nom` property is the same
 * case-sensitive string, so a simple map lookup suffices for 96 metropolitan
 * departments. Overseas departments would need code matching — deferred.
 */
import { useEffect, useState, useMemo } from 'react';
import { GeoJSON, Tooltip } from 'react-leaflet';
import { toast } from 'sonner';

const BOUNDARIES_URL = 'https://cdn.jsdelivr.net/gh/gregoiredavid/france-geojson@master/departements.geojson';

export default function DepartementHeatmap({ addresses, onDepartementClick, activeDepartement }) {
  const [geojson, setGeojson] = useState(null);

  useEffect(() => {
    let ok = true;
    fetch(BOUNDARIES_URL, { credentials: 'omit' })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => { if (ok) setGeojson(data); })
      .catch(() => { if (ok) toast.error('GeoJSON départements indisponible'); });
    return () => { ok = false; };
  }, []);

  const countByName = useMemo(() => {
    const c = new Map();
    for (const a of addresses || []) {
      const n = (a.geo_meta || {}).departement;
      if (n) c.set(n, (c.get(n) || 0) + 1);
    }
    return c;
  }, [addresses]);

  const maxCount = useMemo(
    () => Math.max(1, ...Array.from(countByName.values())),
    [countByName],
  );

  if (!geojson) return null;

  const colorFor = (count) => {
    if (!count) return '#233042'; // slate — no address
    const t = Math.min(1, count / maxCount);
    // Emerald ramp
    if (t < 0.2) return '#065f46';
    if (t < 0.5) return '#10b981';
    if (t < 0.8) return '#34d399';
    return '#6ee7b7';
  };

  const style = (feature) => {
    const name = feature?.properties?.nom;
    const count = countByName.get(name) || 0;
    const active = activeDepartement === name;
    return {
      fillColor: colorFor(count),
      fillOpacity: count ? 0.55 : 0.15,
      weight: active ? 3 : 0.5,
      color: active ? '#fbbf24' : '#334155',
      opacity: 0.8,
    };
  };

  const onEachFeature = (feature, layer) => {
    layer.on('click', () => {
      const name = feature?.properties?.nom;
      const count = countByName.get(name) || 0;
      if (count > 0 && onDepartementClick) onDepartementClick(name);
    });
  };

  return (
    <GeoJSON
      data={geojson}
      style={style}
      onEachFeature={onEachFeature}
      key={`${countByName.size}-${activeDepartement || ''}`}
    >
      <Tooltip sticky>
        {(layer) => {
          const name = layer?.feature?.properties?.nom || '';
          const c = countByName.get(name) || 0;
          return `${name} — ${c} adresse${c > 1 ? 's' : ''}`;
        }}
      </Tooltip>
    </GeoJSON>
  );
}
